// Issue with watermelonDB library https://github.com/Nozbe/WatermelonDB/issues/629
// @ts-nocheck
import { Model, Q } from '@nozbe/watermelondb';
import {
    field,
    date,
    readonly,
    relation,
    lazy,
} from '@nozbe/watermelondb/decorators';
import { setGenerator } from '@nozbe/watermelondb/utils/common/randomId';
import { v4 as uuidv4 } from 'uuid';

setGenerator(uuidv4);

export default class Contact extends Model {
    static table = 'contacts';

    static associations = {
        horse_contacts: { type: 'has_many', foreignKey: 'contact_id' },
        event_contacts: { type: 'has_many', foreignKey: 'contact_id' },
        organisations: { type: 'belongs_to', key: 'organisation_id' },
        invoices: { type: 'has_many', foreignKey: 'contact_id' },
        locations: { type: 'belongs_to', key: 'location_id' },
    };

    @lazy
    horses = this.collections
        .get('horses')
        .query(Q.on('horse_contacts', 'contact_id', this.id));

    @lazy
    events = this.collections
        .get('events')
        .query(Q.on('event_contacts', 'contact_id', this.id));

    @lazy
    invoices = this.collections
        .get('invoices')
        .query(Q.where('contact_id', this.id));

    @lazy
    locations = this.collections
        .get('locations')
        .query(Q.where('id', this.locationId));

    @field('first_name') firstName;
    @field('last_name') lastName;
    @field('description') description;
    @field('role') role;
    @field('business_name') businessName;
    @field('phone') phone;
    @field('phone_prefix') phonePrefix;
    @field('opt_in_status') optInStatus;
    @field('email') email;
    @field('address') address;
    @field('comments') comments;
    @field('location_id') locationId;
    @field('billable') billable;
    @field('hidden') hidden;
    @field('user_id') userId;
    @field('organisation_id') organisationId;
    @field('synced') synced;

    @relation('locations', 'location_id') location;
    @relation('organisations', 'organisation_id') organisation;

    @date('created_at') createdAt;
    @date('updated_at') updatedAt;
}

export type ContactSimple = {
    id: string;
    userId: string;
    organisationId: string;
    firstName: string;
    lastName: string;
    description: string;
    role: string;
    businessName: string;
    phone: string;
    phonePrefix: string;
    optInStatus: string;
    email: string;
    address: string;
    comments: string;
    locationId: string;
    billable: bool;
    hidden: bool;
    synced: bool;
    createdAt: string;
    updatedAt: string;
};

// Timestamps are `Date` on the model but already ISO strings on ContactSimple.
// Screens mix both shapes in one list (DB records + contacts passed through
// navigation params), so simplifying an already simplified contact has to work.
const toISOString = (timestamp: Date | string | null | undefined): string => {
    if (typeof timestamp === 'string') {
        return timestamp;
    }

    return (timestamp ?? new Date()).toISOString();
};

export function simplifyContact(
    contact: ContactModel | ContactSimple,
): ContactSimple {
    return {
        id: contact.id,
        userId: contact.userId,
        organisationId: contact.organisationId,
        firstName: contact.firstName,
        lastName: contact.lastName,
        description: contact.description,
        role: contact.role,
        businessName: contact.businessName,
        phone: contact.phone,
        phonePrefix: contact.phonePrefix,
        optInStatus: contact.optInStatus,
        email: contact.email,
        address: contact.address,
        comments: contact.comments,
        locationId: contact.locationId,
        billable: contact.billable,
        hidden: contact.hidden,
        synced: contact.synced,
        createdAt: toISOString(contact.createdAt),
        updatedAt: toISOString(contact.updatedAt),
    };
}

// Create an actual instance matching keys defined in ContactSimple
// at runtime to be used for dynamic type checking.
export const referenceContact: Record<keyof ContactSimple, undefined> = {
    id: undefined,
    userId: undefined,
    organisationId: undefined,
    firstName: undefined,
    lastName: undefined,
    description: undefined,
    role: undefined,
    businessName: undefined,
    phone: undefined,
    phonePrefix: undefined,
    optInStatus: undefined,
    email: undefined,
    address: undefined,
    comments: undefined,
    locationId: undefined,
    billable: undefined,
    hidden: undefined,
    synced: undefined,
    createdAt: undefined,
    updatedAt: undefined,
};
